import { useEffect, useRef, useState } from 'react';
import Sheet from '@/components/ui/Sheet';
import { buscarAguardando, type AguardandoLinha } from '@/lib/presencial';

interface Props {
  eventoId: number;
  aberto: boolean;
  aoValidar: (codigo: string) => void;
  aoFechar: () => void;
}

// "Não leu o QR?": busca por nome ou CPF quem ainda não foi bipado e deixa
// validar dali mesmo (com uma conferência de nome e idade antes).
export default function FolhaBuscar({ eventoId, aberto, aoValidar, aoFechar }: Props) {
  const [texto, setTexto] = useState('');
  const [linhas, setLinhas] = useState<AguardandoLinha[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState<AguardandoLinha | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (aberto) inputRef.current?.focus();
    else {
      setTexto('');
      setLinhas([]);
      setBuscou(false);
    }
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const t = texto.trim();
    // Com a busca vazia, lista todo mundo que ainda não foi bipado (mais antigo primeiro).
    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const r = await buscarAguardando(eventoId, t);
        setLinhas(r);
        setErro(null);
      } catch {
        setLinhas([]);
        setErro('Não conseguimos buscar agora. Tente de novo.');
      } finally {
        setBuscando(false);
        setBuscou(true);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [texto, eventoId, aberto]);

  return (
    <>
      <Sheet aberto={aberto} titulo="Quem não foi bipado ainda" aoFechar={aoFechar}>
        <input
          ref={inputRef}
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Nome ou CPF"
          autoComplete="off"
          className="h-11 w-full rounded-full border border-background-200 bg-background-50 px-4 text-[15px] text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        />
        {erro && <p className="mt-2 text-[14px] text-foreground-600">{erro}</p>}

        <div className="mt-3 space-y-2">
          {buscando && <p className="py-3 text-[14px] text-foreground-500">Buscando…</p>}
          {!buscando && buscou && linhas.length === 0 && !erro && (
            <p className="py-6 text-center text-[14px] text-foreground-500">
              Ninguém aguardando validação.
            </p>
          )}
          {linhas.map((l) => (
            <div
              key={l.codigo}
              className="flex items-center justify-between gap-3 rounded-[16px] border border-background-200 bg-background-50 px-4 py-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold text-foreground-950">
                  {l.nome}
                </span>
                <span className="mt-0.5 block truncate text-[13px] text-foreground-500">
                  {[l.modalidade, l.grupo, l.idade != null ? `${l.idade} anos` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setConferindo(l)}
                className="aperta shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-success-600 px-4 py-2 text-[14px] font-bold text-white transition-colors hover:bg-success-700"
              >
                Validar
              </button>
            </div>
          ))}
        </div>
      </Sheet>

      <Sheet aberto={!!conferindo} titulo="Confere a pessoa?" aoFechar={() => setConferindo(null)}>
        {conferindo && (
          <>
            <p className="text-[20px] font-extrabold text-foreground-950">{conferindo.nome}</p>
            <p className="mt-1 text-[15px] text-foreground-600">
              {[conferindo.idade != null ? `${conferindo.idade} anos` : null, conferindo.grupo]
                .filter(Boolean)
                .join(' · ') || 'Sem idade cadastrada'}
            </p>
            <p className="mt-3 text-[14px] text-foreground-500">
              Só valide se for essa pessoa na sua frente.
            </p>
            <button
              type="button"
              onClick={() => {
                const codigo = conferindo.codigo;
                setConferindo(null);
                aoValidar(codigo);
              }}
              className="aperta mt-5 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-success-600 text-[15px] font-bold text-white transition-colors hover:bg-success-700"
            >
              Validar esta pessoa
            </button>
          </>
        )}
      </Sheet>
    </>
  );
}